import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Configure, InstantSearch, useRefinementList, useSearchBox } from 'react-instantsearch';
import { createInstantSearchRouterNext } from 'react-instantsearch-router-nextjs';
import { type SearchClient } from 'algoliasearch/lite';
import { type UiState } from 'instantsearch.js';
import { stringify } from 'qs';
import {
  emptySearchResponse,
  isTokenlessQuery,
  shouldSuppressEmptySearch,
} from '@/utilities/search/suppressEmptySearch';
import { createSearchUrl, parseSearchFilter, stateMapping, toFilterArray } from '@/utilities/search/stateMapping';
import { useIsSearching } from '@/hooks/data/search/useIsSearching';
import { useSearch as useFacets } from '@/hooks/search/useSearch';
import { useSearchClient } from '@/context/SearchClientContext';
import { useCurrentPage } from '@/hooks/useCurrentPage';
import singletonRouter, { useRouter } from 'next/router';

interface RefinementLists {
  [attribute: string]: Array<string>;
}

/**
 * Attributes GET /facets never enumerates: tags.facets.__none__ is a frontend-only
 * addition (see search-options.tsx), and the rest are base Typesense fields with no
 * admin-toggleable facet definition of their own.
 */
const ALWAYS_VALID_ATTRIBUTES = new Set([
  'rating',
  'trashed',
  'tags.public',
  'tags.searchable',
  'tags.facets.__none__',
]);

type SearchContextType = {
  navigateToResults: (uiState: UiState) => Promise<boolean>;
  showDetails: boolean;
  setShowDetails: Dispatch<SetStateAction<boolean>>;
};

const SearchContext = createContext<SearchContextType | undefined>(undefined);

const VirtualSearchBox = () => {
  useSearchBox(); // This initializes the search box logic without the UI component
  return null;
};

const VirtualRefinementList = ({ attribute }: { attribute: any }) => {
  useRefinementList({ attribute });
  return null;
};

export const SearchProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();

  const [showDetails, setShowDetails] = useState(false);
  const search = useIsSearching();
  const searchActiveRef = useRef(search);
  useEffect(() => {
    searchActiveRef.current = search;
  }, [search]);

  const { searchClient: baseSearchClient, searchIndexes } = useSearchClient();

  const searchClient = useMemo(
    () =>
      ({
        ...baseSearchClient,
        search(requests: Parameters<SearchClient['search']>[0]) {
          const suppress =
            shouldSuppressEmptySearch(requests, searchActiveRef.current) ||
            requests.every((req) => isTokenlessQuery(req.params?.query));

          if (suppress) {
            return Promise.resolve(emptySearchResponse(requests));
          }
          return baseSearchClient.search(requests);
        },
      }) as unknown as SearchClient,
    [baseSearchClient],
  );

  const navigateToResults = useCallback(
    (uiState: UiState) => {
      const slugs = router.query.slug;
      const path = Array.isArray(slugs) && slugs.length > 0 ? `/browse/${slugs.join('/')}` : '/browse';

      return router.push({
        pathname: path,
        query: stringify(stateMapping(searchIndexes.default).stateToRoute(uiState), { arrayFormat: 'repeat' }),
      });
    },
    [searchIndexes, router],
  );
  const filtersFromQuery = router.query.filters ? String(router.query.filters) : '';

  const { isPublicSharePage, isPublicRoute, isSubmitPage } = useCurrentPage();
  /** Gate on page type, not `search` — the browse sidebar's filters need facets even
   * before a query is typed. */
  const { searchFacets } = useFacets({ enabled: !isPublicSharePage && !isPublicRoute && !isSubmitPage });

  // Every attribute GET /facets currently backs, e.g. metadata.<slug>, workflows.<slug>,
  // tags.facets.<categorySlug> — plus the fixed set it never enumerates (see above).
  const validAttributes = useMemo(() => {
    const attributes = new Set(ALWAYS_VALID_ATTRIBUTES);

    for (const [groupKey, facet] of Object.entries(searchFacets ?? {})) {
      const options = (facet as { options?: Record<string, unknown> })?.options ?? {};
      for (const optionKey of Object.keys(options)) {
        attributes.add(groupKey === 'custom' ? optionKey : `${groupKey}.${optionKey}`);
      }
    }

    return attributes;
  }, [searchFacets]);
  // A response always has at least a non-empty 'version' group, so non-empty here
  // reliably means one has landed rather than the pending-query default of {}.
  const facetsHaveLoaded = Object.keys(searchFacets ?? {}).length > 0;

  /**
   * Anchors every URL-refined attribute with a widget immediately, so state for one
   * whose real widget mounts later (after async facet data loads) isn't dropped first.
   */
  const refinementLists: RefinementLists = {};

  for (const filter of toFilterArray(router.query.searchFilters)) {
    const parsed = parseSearchFilter(filter);

    if (!parsed) {
      continue;
    }

    const [attribute, value] = parsed;
    // Drop a now-stale attribute, but only once confirmed against a loaded response.
    if (facetsHaveLoaded && !validAttributes.has(attribute)) {
      continue;
    }

    (refinementLists[attribute] ??= []).push(value);
  }

  /**
   * A widget's dispose() leaves shared state alone (future.preserveSharedStateOnUnmount),
   * so an unanchored stale attribute still lingers — the URL itself needs correcting,
   * since that's what routeToState re-derives ui state from on every change.
   */
  useEffect(() => {
    if (!facetsHaveLoaded) {
      return;
    }

    const currentFilters = toFilterArray(router.query.searchFilters);
    const cleanedFilters = currentFilters.filter((filter) => {
      const parsed = parseSearchFilter(filter);
      return !parsed || validAttributes.has(parsed[0]);
    });

    if (cleanedFilters.length === currentFilters.length) {
      return;
    }

    const nextQuery = { ...router.query };
    if (cleanedFilters.length > 0) {
      nextQuery.searchFilters = cleanedFilters;
    } else {
      delete nextQuery.searchFilters;
    }

    void router.replace({ pathname: router.pathname, query: nextQuery }, undefined, { shallow: true });
  }, [facetsHaveLoaded, validAttributes, router]);

  return (
    <SearchContext.Provider
      value={{
        navigateToResults,
        showDetails,
        setShowDetails,
      }}
    >
      <InstantSearch
        key={search ? 'search-active' : 'search-inactive'}
        searchClient={searchClient}
        indexName={searchIndexes.default}
        routing={
          search
            ? {
                router: createInstantSearchRouterNext({
                  singletonRouter,
                  routerOptions: {
                    cleanUrlOnDispose: false,

                    // When navigating to an asset detail view (by double clicking on it),
                    // Gallery sends origin=browse, pageIndex=1, and pageSize=25.
                    // About 100 ms later, InstantSearch writes a new asset URL containing only query and filters,
                    // deleting the return metadata. That's why we need a special case when navigating to /asset/.
                    createURL: ({ routeState, location }) =>
                      location.pathname.startsWith('/asset/') ? location.href : createSearchUrl(routeState, location),
                  },
                }),
                stateMapping: stateMapping(searchIndexes.default),
              }
            : undefined
        }
        future={{ preserveSharedStateOnUnmount: true }}
      >
        {/* Widgets are required for UI and Route change, do NOT remove */}
        <Configure attributesToRetrieve={['*']} filters={filtersFromQuery} />
        <VirtualSearchBox />
        {Object.keys(refinementLists).map((attribute, index) => (
          <VirtualRefinementList key={index} attribute={attribute} />
        ))}
        {children}
      </InstantSearch>
    </SearchContext.Provider>
  );
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};
